import React from 'react';
import Layout from '../components/Layout/Layout';

const Privacy = () => {
  return (
    <Layout
      title="Privacy Policy | Prix du Canada"
      description="Privacy policy for Prix du Canada ballet competition - how we collect, use, and protect your personal information."
      keywords="privacy policy, data protection, prix du canada privacy"
    >
      <section className="section" style={{background: 'var(--warm-white)', paddingTop: '4rem', paddingBottom: '4rem'}}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h1 style={{
              fontFamily: "'Playfair Display', 'Georgia', serif",
              fontSize: '2.5rem',
              marginBottom: '1rem',
              color: 'var(--charcoal)'
            }}>
              Privacy Policy
            </h1>

            <p style={{ color: 'var(--soft-gray)', marginBottom: '2rem' }}>
              <strong>Last Updated:</strong> December 2025
            </p>

            <div style={{ lineHeight: 1.8, color: 'var(--charcoal)' }}>

              <h2 style={{
                fontFamily: "'Playfair Display', 'Georgia', serif",
                fontSize: '1.8rem',
                marginTop: '2rem',
                marginBottom: '1rem',
                color: 'var(--charcoal)'
              }}>
                1. Introduction
              </h2>
              <p>
                Prix du Canada ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website prixducanada.com and use our services, including registration for our ballet competition.
              </p>
              <p>
                Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site or use our services.
              </p>

              <h2 style={{
                fontFamily: "'Playfair Display', 'Georgia', serif",
                fontSize: '1.8rem',
                marginTop: '2rem',
                marginBottom: '1rem',
                color: 'var(--charcoal)'
              }}>
                2. Information We Collect
              </h2>

              <h3 style={{
                fontSize: '1.3rem',
                marginTop: '1.5rem',
                marginBottom: '0.75rem',
                color: 'var(--charcoal)'
              }}>
                2.1 Personal Information
              </h3>
              <p>We may collect personal information that you voluntarily provide to us when you:</p>
              <ul style={{ marginLeft: '2rem', marginBottom: '1rem', textAlign: 'left' }}>
                <li>Register for an account</li>
                <li>Register for the competition</li>
                <li>Subscribe to our newsletter</li>
                <li>Contact us via email or contact form</li>
                <li>Sign in using Google OAuth</li>
              </ul>
              <p>This information may include:</p>
              <ul style={{ marginLeft: '2rem', marginBottom: '1rem', textAlign: 'left' }}>
                <li>Name and contact information (email address, phone number, mailing address)</li>
                <li>Date of birth and age</li>
                <li>Gender</li>
                <li>Dance background and training information</li>
                <li>School/studio affiliation</li>
                <li>Emergency contact information</li>
                <li>Medical information (allergies, conditions relevant to participation)</li>
                <li>Payment information (processed securely through our payment processor)</li>
                <li>Profile information from Google if you use Google Sign-In (name, email, profile picture)</li>
              </ul>

              <h3 style={{
                fontSize: '1.3rem',
                marginTop: '1.5rem',
                marginBottom: '0.75rem',
                color: 'var(--charcoal)'
              }}>
                2.2 Automatically Collected Information
              </h3>
              <p>When you visit our website, we may automatically collect certain information about your device, including:</p>
              <ul style={{ marginLeft: '2rem', marginBottom: '1rem', textAlign: 'left' }}>
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Referring website</li>
                <li>Pages visited and time spent on pages</li>
                <li>Click patterns and navigation paths</li>
              </ul>

              <h2 style={{
                fontFamily: "'Playfair Display', 'Georgia', serif",
                fontSize: '1.8rem',
                marginTop: '2rem',
                marginBottom: '1rem',
                color: 'var(--charcoal)'
              }}>
                3. How We Use Your Information
              </h2>
              <p>We use the information we collect to:</p>
              <ul style={{ marginLeft: '2rem', marginBottom: '1rem', textAlign: 'left' }}>
                <li>Process your competition registration and manage your participation</li>
                <li>Create and manage your account</li>
                <li>Communicate with you about the competition, workshops, and events</li>
                <li>Send you newsletters and promotional materials (with your consent)</li>
                <li>Process payments and prevent fraud</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Improve our website and services</li>
                <li>Comply with legal obligations</li>
                <li>Ensure safety and security of participants</li>
              </ul>

              <h2 style={{
                fontFamily: "'Playfair Display', 'Georgia', serif",
                fontSize: '1.8rem',
                marginTop: '2rem',
                marginBottom: '1rem',
                color: 'var(--charcoal)'
              }}>
                4. Third-Party Services
              </h2>

              <h3 style={{
                fontSize: '1.3rem',
                marginTop: '1.5rem',
                marginBottom: '0.75rem',
                color: 'var(--charcoal)'
              }}>
                4.1 Google OAuth
              </h3>
              <p>
                We use Google OAuth for authentication. When you sign in with Google, we receive your basic profile information (name, email address, and profile picture) as authorized by you. Google's use of information received from Google APIs will adhere to <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-gold)' }}>Google API Services User Data Policy</a>, including the Limited Use requirements.
              </p>

              <h3 style={{
                fontSize: '1.3rem',
                marginTop: '1.5rem',
                marginBottom: '0.75rem',
                color: 'var(--charcoal)'
              }}>
                4.2 Authentication & Database
              </h3>
              <p>
                We use Supabase for secure authentication and data storage. All data is encrypted in transit and at rest.
              </p>

              <h3 style={{
                fontSize: '1.3rem',
                marginTop: '1.5rem',
                marginBottom: '0.75rem',
                color: 'var(--charcoal)'
              }}>
                4.3 Email Services
              </h3>
              <p>
                We use Mailchimp and SendGrid for email communications. Your email address may be shared with these services to send you newsletters, competition updates, and transactional emails.
              </p>

              <h3 style={{
                fontSize: '1.3rem',
                marginTop: '1.5rem',
                marginBottom: '0.75rem',
                color: 'var(--charcoal)'
              }}>
                4.4 Payment Processing
              </h3>
              <p>
                Payment information is processed securely through our payment processor. We do not store complete credit card information on our servers.
              </p>

              <h2 style={{
                fontFamily: "'Playfair Display', 'Georgia', serif",
                fontSize: '1.8rem',
                marginTop: '2rem',
                marginBottom: '1rem',
                color: 'var(--charcoal)'
              }}>
                5. Data Security
              </h2>
              <p>
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>

              <h2 style={{
                fontFamily: "'Playfair Display', 'Georgia', serif",
                fontSize: '1.8rem',
                marginTop: '2rem',
                marginBottom: '1rem',
                color: 'var(--charcoal)'
              }}>
                6. Your Rights
              </h2>
              <p>Depending on your location, you may have the following rights:</p>
              <ul style={{ marginLeft: '2rem', marginBottom: '1rem', textAlign: 'left' }}>
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Objection:</strong> Object to our processing of your personal information</li>
                <li><strong>Data Portability:</strong> Request transfer of your data to another service</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent for newsletter subscriptions at any time</li>
              </ul>
              <p>
                To exercise these rights, please contact us at <a href="mailto:info@prixducanada.com" style={{ color: 'var(--primary-gold)' }}>info@prixducanada.com</a>.
              </p>

              <h2 style={{
                fontFamily: "'Playfair Display', 'Georgia', serif",
                fontSize: '1.8rem',
                marginTop: '2rem',
                marginBottom: '1rem',
                color: 'var(--charcoal)'
              }}>
                7. Children's Privacy
              </h2>
              <p>
                Prix du Canada is a ballet competition that includes participants under the age of 18. We collect personal information from minors only with parental or guardian consent. Parents and guardians have the right to review, modify, or request deletion of their child's information by contacting us.
              </p>

              <h2 style={{
                fontFamily: "'Playfair Display', 'Georgia', serif",
                fontSize: '1.8rem',
                marginTop: '2rem',
                marginBottom: '1rem',
                color: 'var(--charcoal)'
              }}>
                8. Data Retention
              </h2>
              <p>
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Competition registration data may be retained for archival and historical purposes.
              </p>

              <h2 style={{
                fontFamily: "'Playfair Display', 'Georgia', serif",
                fontSize: '1.8rem',
                marginTop: '2rem',
                marginBottom: '1rem',
                color: 'var(--charcoal)'
              }}>
                9. International Data Transfers
              </h2>
              <p>
                Your information may be transferred to and processed in countries other than your country of residence. We ensure that appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
              </p>

              <h2 style={{
                fontFamily: "'Playfair Display', 'Georgia', serif",
                fontSize: '1.8rem',
                marginTop: '2rem',
                marginBottom: '1rem',
                color: 'var(--charcoal)'
              }}>
                10. Cookies and Tracking
              </h2>
              <p>
                We use cookies and similar tracking technologies to enhance your experience on our website. You can control cookie settings through your browser preferences. Note that disabling cookies may affect website functionality.
              </p>

              <h2 style={{
                fontFamily: "'Playfair Display', 'Georgia', serif",
                fontSize: '1.8rem',
                marginTop: '2rem',
                marginBottom: '1rem',
                color: 'var(--charcoal)'
              }}>
                11. Changes to This Privacy Policy
              </h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
              </p>

              <h2 style={{
                fontFamily: "'Playfair Display', 'Georgia', serif",
                fontSize: '1.8rem',
                marginTop: '2rem',
                marginBottom: '1rem',
                color: 'var(--charcoal)'
              }}>
                12. Contact Us
              </h2>
              <p>
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
              </p>
              <ul style={{ marginLeft: '2rem', marginBottom: '1rem', listStyle: 'none', textAlign: 'left' }}>
                <li><strong>Email:</strong> <a href="mailto:info@prixducanada.com" style={{ color: 'var(--primary-gold)' }}>info@prixducanada.com</a></li>
                <li><strong>Website:</strong> <a href="https://prixducanada.com" style={{ color: 'var(--primary-gold)' }}>prixducanada.com</a></li>
              </ul>

              <div style={{
                marginTop: '3rem',
                padding: '1.5rem',
                background: 'var(--soft-cream)',
                borderLeft: '4px solid var(--primary-gold)',
                borderRadius: '4px'
              }}>
                <p style={{ margin: 0, fontSize: '0.95rem' }}>
                  <strong>Google API Services User Data Policy Compliance:</strong> Prix du Canada's use and transfer of information received from Google APIs to any other app will adhere to <a href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-gold)' }}>Google API Services User Data Policy</a>, including the Limited Use requirements.
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Privacy;
